import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { User, Lock, CheckCircle, AlertCircle } from "lucide-react";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { MainLayout } from "@/components/layout/MainLayout";
import { formatDisplayName, getDisplayInitial } from "@/lib/utils";
import { useProfile } from "@/features/auth/hooks/useProfile";

// Profile form schema
const profileSchema = z.object({
    firstName: z
        .string()
        .max(100, "First name must be less than 100 characters")
        .optional()
        .or(z.literal("")),
    middleName: z
        .string()
        .max(100, "Middle name must be less than 100 characters")
        .optional()
        .or(z.literal("")),
    lastName: z
        .string()
        .max(100, "Last name must be less than 100 characters")
        .optional()
        .or(z.literal("")),
    mobile: z
        .string()
        .regex(/^[\d\s\-+()]*$/, "Invalid phone number format")
        .optional()
        .or(z.literal("")),
});

// Password form schema
const passwordSchema = z
    .object({
        currentPassword: z.string().min(1, "Current password is required"),
        newPassword: z
            .string()
            .min(8, "Password must be at least 8 characters")
            .regex(
                /[A-Z]/,
                "Password must contain at least one uppercase letter"
            )
            .regex(
                /[a-z]/,
                "Password must contain at least one lowercase letter"
            )
            .regex(/[0-9]/, "Password must contain at least one number"),
        confirmPassword: z.string().min(1, "Please confirm your password"),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: "Passwords do not match",
        path: ["confirmPassword"],
    });

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

// Password strength calculator
const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[a-z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^A-Za-z0-9]/.test(password)) strength += 10;
    return Math.min(strength, 100);
};

const getStrengthColor = (strength: number): string => {
    if (strength < 30) return "bg-destructive";
    if (strength < 60) return "bg-warning";
    if (strength < 80) return "bg-info";
    return "bg-success";
};

const getStrengthLabel = (strength: number): string => {
    if (strength < 30) return "Weak";
    if (strength < 60) return "Fair";
    if (strength < 80) return "Good";
    return "Strong";
};

export const ProfilePage = () => {
    const {
        profile,
        isLoading,
        isUpdating,
        isChangingPassword,
        updateProfile,
        changePassword,
    } = useProfile();

    const profileForm = useForm<ProfileFormData>({
        resolver: zodResolver(profileSchema),
        values: {
            firstName: profile?.firstName || "",
            middleName: profile?.middleName || "",
            lastName: profile?.lastName || "",
            mobile: profile?.mobile || "",
        },
    });

    const passwordForm = useForm<PasswordFormData>({
        resolver: zodResolver(passwordSchema),
        defaultValues: {
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
    });

    const newPassword = passwordForm.watch("newPassword");
    const passwordStrength = calculatePasswordStrength(newPassword || "");

    const handleProfileSubmit = async (data: ProfileFormData) => {
        await updateProfile({
            firstName: data.firstName || undefined,
            middleName: data.middleName || undefined,
            lastName: data.lastName || undefined,
            mobile: data.mobile || undefined,
        });
    };

    const handlePasswordSubmit = async (data: PasswordFormData) => {
        const success = await changePassword({
            currentPassword: data.currentPassword,
            newPassword: data.newPassword,
        });
        if (success) {
            passwordForm.reset();
        }
    };

    if (isLoading) {
        return (
            <MainLayout>
                <div className="container mx-auto py-6 px-4 sm:py-8 max-w-3xl">
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <Skeleton className="h-9 w-56" />
                            <Skeleton className="h-4 w-80 max-w-full" />
                        </div>
                        <div className="flex gap-2">
                            <Skeleton className="h-11 w-32 rounded-md" />
                            <Skeleton className="h-11 w-24 rounded-md" />
                        </div>
                        <Card>
                            <CardHeader className="pb-4">
                                <Skeleton className="h-6 w-36" />
                                <Skeleton className="h-4 w-64 mt-1" />
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="flex items-center gap-4 sm:gap-6">
                                    <Skeleton className="h-20 w-20 sm:h-24 sm:w-24 rounded-full shrink-0" />
                                    <div className="space-y-2 flex-1">
                                        <Skeleton className="h-5 w-32" />
                                        <Skeleton className="h-4 w-48" />
                                        <Skeleton className="h-3 w-24" />
                                    </div>
                                </div>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="space-y-2">
                                            <Skeleton className="h-4 w-20" />
                                            <Skeleton className="h-10 w-full rounded-md" />
                                        </div>
                                    ))}
                                </div>
                                <Skeleton className="h-10 w-28 rounded-md" />
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container mx-auto py-6 px-4 sm:py-8 max-w-3xl content-loaded">
                <div className="space-y-6">
                    {/* Page Header */}
                    <div className="space-y-1">
                        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                            Profile Settings
                        </h1>
                        <p className="text-muted-foreground">
                            Manage your account settings and security
                            preferences
                        </p>
                    </div>

                    <Tabs defaultValue="profile" className="space-y-6">
                        <TabsList className="grid w-full grid-cols-2 h-11">
                            <TabsTrigger
                                value="profile"
                                className="gap-2 text-sm"
                            >
                                <User className="h-4 w-4" />
                                <span className="hidden sm:inline">
                                    Profile Details
                                </span>
                                <span className="sm:hidden">Profile</span>
                            </TabsTrigger>
                            <TabsTrigger
                                value="security"
                                className="gap-2 text-sm"
                            >
                                <Lock className="h-4 w-4" />
                                Security
                            </TabsTrigger>
                        </TabsList>

                        {/* Profile Details Tab */}
                        <TabsContent value="profile">
                            <Card>
                                <CardHeader className="pb-4">
                                    <CardTitle className="text-lg">
                                        Profile Details
                                    </CardTitle>
                                    <CardDescription>
                                        Update your personal information
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Profile summary */}
                                    <div className="flex items-center gap-4 sm:gap-6">
                                        <Avatar className="h-20 w-20 sm:h-24 sm:w-24 border-2 border-border">
                                            <AvatarFallback className="text-xl sm:text-2xl bg-primary/10 text-primary font-semibold">
                                                {getDisplayInitial(profile)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="font-semibold text-foreground">
                                                {formatDisplayName(profile) || profile?.userId}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {profile?.userId}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1 capitalize">
                                                {profile?.role?.replace(
                                                    "role_",
                                                    ""
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Profile Form */}
                                    <form
                                        onSubmit={profileForm.handleSubmit(
                                            handleProfileSubmit
                                        )}
                                        className="space-y-5"
                                    >
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div className="space-y-2">
                                                <Label htmlFor="firstName">
                                                    First Name
                                                </Label>
                                                <Input
                                                    id="firstName"
                                                    placeholder="John"
                                                    {...profileForm.register(
                                                        "firstName"
                                                    )}
                                                />
                                                {profileForm.formState.errors
                                                    .firstName && (
                                                    <p className="text-sm text-destructive flex items-center gap-1.5">
                                                        <AlertCircle className="h-3.5 w-3.5" />
                                                        {
                                                            profileForm
                                                                .formState
                                                                .errors.firstName
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="lastName">
                                                    Last Name
                                                </Label>
                                                <Input
                                                    id="lastName"
                                                    placeholder="Doe"
                                                    {...profileForm.register(
                                                        "lastName"
                                                    )}
                                                />
                                                {profileForm.formState.errors
                                                    .lastName && (
                                                    <p className="text-sm text-destructive flex items-center gap-1.5">
                                                        <AlertCircle className="h-3.5 w-3.5" />
                                                        {
                                                            profileForm
                                                                .formState
                                                                .errors.lastName
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                            <div className="space-y-2 sm:col-span-2">
                                                <Label htmlFor="middleName">
                                                    Middle Name
                                                </Label>
                                                <Input
                                                    id="middleName"
                                                    placeholder=""
                                                    {...profileForm.register(
                                                        "middleName"
                                                    )}
                                                />
                                            </div>
                                            <div className="space-y-2 sm:col-span-2">
                                                <Label htmlFor="email">
                                                    Email
                                                </Label>
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    placeholder="you@example.com"
                                                    value={
                                                        profile?.userId || ""
                                                    }
                                                    disabled
                                                    className="bg-muted"
                                                />
                                                <p className="text-xs text-muted-foreground">
                                                    Email cannot be changed
                                                </p>
                                            </div>

                                            <div className="space-y-2 sm:col-span-2">
                                                <Label htmlFor="mobile">
                                                    Phone Number
                                                </Label>
                                                <Input
                                                    id="mobile"
                                                    placeholder="+1 (555) 123-4567"
                                                    {...profileForm.register(
                                                        "mobile"
                                                    )}
                                                />
                                                {profileForm.formState.errors
                                                    .mobile && (
                                                    <p className="text-sm text-destructive flex items-center gap-1.5">
                                                        <AlertCircle className="h-3.5 w-3.5" />
                                                        {
                                                            profileForm
                                                                .formState
                                                                .errors.mobile
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <Button
                                            type="submit"
                                            loading={isUpdating}
                                            loadingLabel="Saving..."
                                        >
                                            Save Changes
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Security Tab */}
                        <TabsContent value="security">
                            <Card>
                                <CardHeader className="pb-4">
                                    <CardTitle className="text-lg">
                                        Change Password
                                    </CardTitle>
                                    <CardDescription>
                                        Update your password to keep your
                                        account secure
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form
                                        onSubmit={passwordForm.handleSubmit(
                                            handlePasswordSubmit
                                        )}
                                        className="space-y-5"
                                    >
                                        <div className="space-y-2">
                                            <Label htmlFor="currentPassword">
                                                Current Password
                                            </Label>
                                            <PasswordInput
                                                id="currentPassword"
                                                placeholder="Enter current password"
                                                {...passwordForm.register(
                                                    "currentPassword"
                                                )}
                                            />
                                            {passwordForm.formState.errors
                                                .currentPassword && (
                                                <p className="text-sm text-destructive flex items-center gap-1.5">
                                                    <AlertCircle className="h-3.5 w-3.5" />
                                                    {
                                                        passwordForm.formState
                                                            .errors
                                                            .currentPassword
                                                            .message
                                                    }
                                                </p>
                                            )}
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="newPassword">
                                                New Password
                                            </Label>
                                            <PasswordInput
                                                id="newPassword"
                                                placeholder="Enter new password"
                                                {...passwordForm.register(
                                                    "newPassword"
                                                )}
                                            />
                                            {newPassword && (
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <Progress
                                                            value={
                                                                passwordStrength
                                                            }
                                                            className={`h-2 flex-1 ${getStrengthColor(passwordStrength)}`}
                                                        />
                                                        <span className="text-xs text-muted-foreground w-12">
                                                            {getStrengthLabel(
                                                                passwordStrength
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            {passwordForm.formState.errors
                                                .newPassword && (
                                                <p className="text-sm text-destructive flex items-center gap-1.5">
                                                    <AlertCircle className="h-3.5 w-3.5" />
                                                    {
                                                        passwordForm.formState
                                                            .errors.newPassword
                                                            .message
                                                    }
                                                </p>
                                            )}
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="confirmPassword">
                                                Confirm New Password
                                            </Label>
                                            <PasswordInput
                                                id="confirmPassword"
                                                placeholder="Confirm new password"
                                                {...passwordForm.register(
                                                    "confirmPassword"
                                                )}
                                            />
                                            {passwordForm.formState.errors
                                                .confirmPassword && (
                                                <p className="text-sm text-destructive flex items-center gap-1.5">
                                                    <AlertCircle className="h-3.5 w-3.5" />
                                                    {
                                                        passwordForm.formState
                                                            .errors
                                                            .confirmPassword
                                                            .message
                                                    }
                                                </p>
                                            )}
                                        </div>

                                        {/* Password Requirements */}
                                        <div className="p-4 bg-muted/50 rounded-lg border border-border">
                                            <p className="text-sm font-medium mb-3 text-foreground">
                                                Password Requirements:
                                            </p>
                                            <ul className="text-sm space-y-2">
                                                <li className="flex items-center gap-2">
                                                    <CheckCircle
                                                        className={`h-4 w-4 ${
                                                            newPassword?.length >=
                                                            8
                                                                ? "text-success"
                                                                : "text-muted-foreground/40"
                                                        }`}
                                                    />
                                                    <span
                                                        className={
                                                            newPassword?.length >=
                                                            8
                                                                ? "text-foreground"
                                                                : "text-muted-foreground"
                                                        }
                                                    >
                                                        At least 8 characters
                                                    </span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <CheckCircle
                                                        className={`h-4 w-4 ${
                                                            /[A-Z]/.test(
                                                                newPassword ||
                                                                    ""
                                                            )
                                                                ? "text-success"
                                                                : "text-muted-foreground/40"
                                                        }`}
                                                    />
                                                    <span
                                                        className={
                                                            /[A-Z]/.test(
                                                                newPassword ||
                                                                    ""
                                                            )
                                                                ? "text-foreground"
                                                                : "text-muted-foreground"
                                                        }
                                                    >
                                                        One uppercase letter
                                                    </span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <CheckCircle
                                                        className={`h-4 w-4 ${
                                                            /[a-z]/.test(
                                                                newPassword ||
                                                                    ""
                                                            )
                                                                ? "text-success"
                                                                : "text-muted-foreground/40"
                                                        }`}
                                                    />
                                                    <span
                                                        className={
                                                            /[a-z]/.test(
                                                                newPassword ||
                                                                    ""
                                                            )
                                                                ? "text-foreground"
                                                                : "text-muted-foreground"
                                                        }
                                                    >
                                                        One lowercase letter
                                                    </span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <CheckCircle
                                                        className={`h-4 w-4 ${
                                                            /[0-9]/.test(
                                                                newPassword ||
                                                                    ""
                                                            )
                                                                ? "text-success"
                                                                : "text-muted-foreground/40"
                                                        }`}
                                                    />
                                                    <span
                                                        className={
                                                            /[0-9]/.test(
                                                                newPassword ||
                                                                    ""
                                                            )
                                                                ? "text-foreground"
                                                                : "text-muted-foreground"
                                                        }
                                                    >
                                                        One number
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>

                                        <Button
                                            type="submit"
                                            loading={isChangingPassword}
                                            loadingLabel="Changing Password..."
                                        >
                                            Change Password
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </MainLayout>
    );
};
